import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminService } from '../../services/adminService';
import { Ban, Check, ArrowLeft } from 'lucide-react';

const AdminUserDetailPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [providerProfile, setProviderProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!userId) return;
    Promise.all([
      adminService.getUser(userId),
      adminService.getProviderProfile(userId),
    ]).then(([p, pp]) => {
      setProfile(p);
      setProviderProfile(pp);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [userId]);

  const handleBlockToggle = async () => {
    if (!profile?.id) return;
    setSaving(true);
    try {
      await adminService.setBlocked(profile.id, !profile.is_blocked);
      setProfile((prev) => ({ ...prev, is_blocked: !prev.is_blocked }));
    } catch (err) {
      console.error(err);
    }
    setSaving(false);
  };

  if (loading || !profile) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => navigate('/admin/users')}
        className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to users
      </button>
      <h1 className="font-heading font-bold text-2xl text-slate-900 mb-6">
        {profile.display_name || profile.username || profile.email}
        {profile.is_dummy && <span className="ml-2 text-sm font-normal text-amber-600">(Dummy profile)</span>}
      </h1>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden mb-6">
        <div className="p-6 border-b border-slate-200 flex flex-wrap items-center gap-4">
          <img
            src={profile.avatar_url || '/default-avatar.svg'}
            alt=""
            className="w-20 h-20 rounded-xl object-cover bg-slate-200"
          />
          <div>
            <p className="font-medium text-slate-900">{profile.display_name}</p>
            <p className="text-slate-600 text-sm">{profile.email}</p>
            <p className="text-slate-500 text-sm">
              Role: {profile.role} · City: {profile.city || '—'} · Verified: {profile.is_verified ? 'Yes' : 'No'}
            </p>
          </div>
        </div>
        <div className="p-6 grid sm:grid-cols-2 gap-4 text-sm">
          <div><span className="text-slate-500">Username</span> {profile.username || '—'}</div>
          <div><span className="text-slate-500">Gender</span> {profile.gender || '—'}</div>
          <div><span className="text-slate-500">Created</span> {profile.created_at ? new Date(profile.created_at).toLocaleString() : '—'}</div>
          <div><span className="text-slate-500">Subscription</span> {profile.subscription_status || '—'}</div>
        </div>
      </div>

      {providerProfile && (
        <div className="rounded-xl border border-slate-200 bg-white p-6 mb-6">
          <h2 className="font-semibold text-slate-900 mb-3">Service provider profile</h2>
          <p className="text-sm text-slate-600">Verification: {providerProfile.verification_status || '—'}</p>
          <p className="text-sm text-slate-600">Hourly rate (paise): {providerProfile.hourly_rate_paise ?? '—'}</p>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="font-semibold text-slate-900 mb-3">Actions</h2>
        <button
          type="button"
          onClick={handleBlockToggle}
          disabled={saving}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium ${
            profile.is_blocked
              ? 'bg-emerald-600 text-white hover:bg-emerald-700'
              : 'bg-red-600 text-white hover:bg-red-700'
          } disabled:opacity-50`}
        >
          <Ban className="h-4 w-4" />
          {profile.is_blocked ? 'Unblock user' : 'Block user'}
        </button>
      </div>
    </div>
  );
};

export default AdminUserDetailPage;
